import { useEffect, useState } from 'react';
import { listSchedules, setSchedule, type Schedule } from '../account';
import SetSchedule from './SetSchedule';

/**
 * The automatic re-checks a client has, one per question set, with the off
 * switch (D-30). New ones are switched on from a set (the scan wizard or the
 * question-sets page); this is the overview.
 */
export default function SchedulesSection({ plan, email }: { plan: 'free' | 'pro'; email: string }): JSX.Element | null {
  const [rows, setRows] = useState<Schedule[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { listSchedules().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return null;

  // Rows from before schedules were per set: still runnable, still switchable off.
  async function toggleLegacy(s: Schedule, enabled: boolean): Promise<void> {
    setBusy(s.id);
    try {
      const next = await setSchedule({ url: s.url, enabled, engine: s.engine, frequency: s.frequency });
      setRows((prev) => (prev ?? []).map((r) => (r.id === next.id ? next : r)));
    } catch { /* leave the row as-is; the switch simply doesn't move */ } finally { setBusy(null); }
  }

  return (
    <div className="mb-6 border border-warm-dark-2 rounded-xl p-4">
      {/* No heading here: this panel IS a dashboard page now, and the page
          head above it already says what this is. */}
      <p className="text-sm text-text-secondary mb-3">
        Each question set can have its own automatic re-check. The same questions are re-asked on the schedule and {email} gets
        an email with what changed. Turn one on from <span className="text-text-primary">Question sets</span> or the last step of a scan.
      </p>
      {rows.length === 0 ? (
        <p className="font-mono text-sm text-text-secondary">No automatic re-checks yet.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rows.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 border border-warm-dark-2 rounded-lg px-3 py-2 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm text-text-primary truncate">
                  {s.domain}
                  <span className="text-text-secondary"> · {s.querySetName ?? 'active set'}</span>
                </p>
                {!s.querySetId && (
                  <p className="font-mono text-xs text-text-secondary">
                    {s.enabled ? `${s.frequency} · next ${new Date(s.nextRunAt).toLocaleDateString()}` : 'off'}
                  </p>
                )}
              </div>
              {s.querySetId ? (
                <SetSchedule set={{ id: s.querySetId, domain: s.domain }} plan={plan} initial={s}
                  onChange={(n) => setRows((prev) => (prev ?? []).map((r) => (r.id === n.id ? n : r)))} />
              ) : (
                <button
                  onClick={() => void toggleLegacy(s, !s.enabled)}
                  disabled={busy === s.id || (plan !== 'pro' && !s.enabled)}
                  className={`shrink-0 font-mono text-sm rounded-lg px-3 py-1.5 border transition disabled:opacity-40 ${
                    s.enabled ? 'bg-success/20 text-success border-success/50' : 'text-cyan border-cyan/40 hover:bg-cyan/10'}`}
                >
                  {busy === s.id ? '…' : s.enabled ? '✓ On' : 'Turn on'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
